/**
 * Google Consent Mode v2 — ADVANCED (Epic 33, Story 33-5).
 *
 * Advanced mode loads tags pre-consent in a cookieless state and UPGRADES them
 * when the user consents — retaining modeled conversions for EEA users who
 * decline (ADR-0005 "Why Advanced Consent Mode"). The app pushes a
 * default-DENIED state to the dataLayer BEFORE GTM loads (defense-in-depth);
 * the Iubenda CMP (wired in GTM, Story 33-4) issues the `consent: update` when
 * the user chooses.
 *
 * The v2 signals: ad_storage / analytics_storage / ad_user_data /
 * ad_personalization (ADR-0005 Decision).
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

export type ConsentSignal =
  | "ad_storage"
  | "analytics_storage"
  | "ad_user_data"
  | "ad_personalization";

export type ConsentValue = "granted" | "denied";
export type ConsentState = Record<ConsentSignal, ConsentValue>;

export const CONSENT_SIGNALS: readonly ConsentSignal[] = [
  "ad_storage",
  "analytics_storage",
  "ad_user_data",
  "ad_personalization",
] as const;

/** Every signal denied — the pre-consent default for Advanced mode. */
export const ALL_DENIED: ConsentState = {
  ad_storage: "denied",
  analytics_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
};

/**
 * gtag shim — pushes the arguments to the dataLayer in the shape GTM /
 * Consent Mode reads (`dataLayer[i][0] === 'consent'`). Mirrors Google's
 * documented `function gtag(){dataLayer.push(arguments)}` (an array has the
 * same positional indexing GTM consumes).
 */
export function gtag(...args: unknown[]): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(args);
}

/** True once a `['consent','default',…]` entry is on the dataLayer. */
export function hasConsentDefault(): boolean {
  if (typeof window === "undefined") return false;
  const dl = window.dataLayer ?? [];
  return dl.some(
    (entry) => Array.isArray(entry) && entry[0] === "consent" && entry[1] === "default",
  );
}

/**
 * Push the Advanced-mode default: all signals denied, `wait_for_update` gives
 * the CMP a window to issue its update before tags fire (cookieless ping only
 * until then). Idempotent — no-op if a default is already present.
 */
export function pushConsentDefaultDenied(waitForUpdateMs = 500): void {
  if (hasConsentDefault()) return;
  gtag("consent", "default", { ...ALL_DENIED, wait_for_update: waitForUpdateMs });
}

/** Upgrade/downgrade consent after the user chooses (CMP calls this via GTM). */
export function updateConsent(state: Partial<ConsentState>): void {
  gtag("consent", "update", state);
}
